/* eslint-disable react/no-danger */
import {
  Avatar, Box, Chip, Divider, Stack, Typography,
} from '@mui/joy';
import React from 'react';
import {
  RiBriefcaseLine,
} from 'react-icons/ri';
import { TbSchool, TbBrandOpenSource } from 'react-icons/tb';
import { IoLanguage } from 'react-icons/io5';
// import { GoOrganization } from 'react-icons/go';
// import { FaHandsHelping } from 'react-icons/fa';
import { VscOrganization } from 'react-icons/vsc';
import details from 'assets/Details';
import {
  Education,
  Experience,
  Affiliation,
  CommunityOps,
  Coursework,
} from 'components/Details';
import Meta from 'components/Meta';
import { useMobileMode } from 'components/Responsive';

export function Languages() {
  const color = (level: string): 'warning' | 'danger' | 'neutral' | 'info' => {
    switch (level) {
      case 'A1':
        return 'warning';
      case 'A2':
        return 'danger';
      case 'B1':
        return 'neutral';
      case 'B2':
        return 'info';
      case 'C1':
        return 'info';
      case 'C2':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Stack direction="row" flexWrap="wrap" gap={2} p={1}>
      {details.languages.map((language) => (
        <Stack
          key={language.name}
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ fontFamily: "'Fira Code', monospace" }}
        >
          <Typography
            component="span"
            sx={{
              color: 'primary.400',
              fontSize: '0.85em',
              fontFamily: "'Fira Code', monospace",
            }}
          >
            {'>_'}
          </Typography>
          <Typography
            level="body2"
            sx={{ fontFamily: "'Fira Code', monospace" }}
          >
            {language.name}
          </Typography>
          <Chip
            size="sm"
            variant="soft"
            color={color(language.level)}
            sx={{
              fontFamily: "'Fira Code', monospace",
              fontSize: '0.7rem',
            }}
          >
            {language.level}
          </Chip>
          {language.native && (
            <Typography
              level="body3"
              sx={{
                fontFamily: "'Fira Code', monospace",
                color: 'text.tertiary',
              }}
            >
              (native)
            </Typography>
          )}
        </Stack>
      ))}
    </Stack>
  );
}

export default function Resume() {
  const mobile = useMobileMode();

  return (
    <>
      <Meta title={`Expertise - ${details.name.first} ${details.name.last}`} />
      <Stack
        alignItems="center"
        sx={{
          width: '100vw',
          paddingTop: 'var(--nav-safe-area-inset-top)',
          paddingBottom: 'var(--nav-safe-area-inset-bottom)',
          paddingLeft: 'var(--nav-safe-area-inset-left)',
        }}
      >
        <Box component="div" maxWidth="65em">
          <Stack
            paddingX={mobile ? 3 : 12}
            paddingY={mobile ? 5 : 6}
            gap={3}
            width="100%"
            height="100%"
          >
            <Stack component="header" gap={0}>
              <Typography level="h2" fontWeight="xl">
                {`${details.name.first} ${details.name.last}`}
              </Typography>
              <Typography level="h6" fontWeight="lg" textColor="text.secondary" marginBottom={0.5}>
                Software Engineer
                <span style={{ color: 'var(--joy-palette-primary-plainColor)' }}>.</span>
              </Typography>
            </Stack>
            <Box
              component="section"
              sx={{
                gap: 3,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={1}>
                {[
                  { key: 'address', label: 'Address' },
                  { key: 'email', label: 'Email' },
                  { key: 'phone', label: 'Phone' },
                  { key: 'linkedin', label: 'LinkedIn' },
                  { key: 'github', label: 'Github' },
                ].map(
                  ({ key, label }) => {
                    const value = details.contact[
                          key as keyof typeof details.contact
                    ];
                    const isUrl = value.startsWith('http');
                    return (
                      <Stack key={key}>
                        <Typography
                          level="body2"
                          fontWeight="bold"
                          textTransform="capitalize"
                        >
                          {label}
                        </Typography>
                        {isUrl ? (
                          <Typography
                            component="a"
                            level="body2"
                            href={value}
                            target="_blank"
                            sx={{
                              wordBreak: 'break-word',
                              textDecoration: 'none',
                              fontFamily: "'Fira Code', monospace",
                              fontSize: '0.8rem',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {value.replace(/^(https?:\/\/)?(www\.)?/, '')}
                          </Typography>
                        ) : (
                          <Typography
                            level="body2"
                            sx={{
                              wordBreak: 'break-word',
                              fontFamily: "'Fira Code', monospace",
                              fontSize: '0.85rem',
                            }}
                          >
                            {value}
                          </Typography>
                        )}
                      </Stack>
                    );
                  },
                )}
              </Stack>
              <Stack gap={1}>
                <Typography
                  level="h6"
                  fontWeight="lg"
                  startDecorator={(
                    <Avatar size="sm" alt="Work experience">
                      <RiBriefcaseLine />
                    </Avatar>
                    )}
                >
                  Experience
                </Typography>
                <Divider />
                <Experience />
              </Stack>

              <Stack gap={1}>
                <Typography
                  level="h6"
                  fontWeight="lg"
                  startDecorator={(
                    <Avatar size="sm" alt="Education">
                      <TbSchool />
                    </Avatar>
                    )}
                >
                  Education
                </Typography>
                <Divider />
                <Stack gap={2}>
                  <Education wrap />
                  <Coursework />
                </Stack>
              </Stack>

              <Stack gap={1}>
                <Typography level="h6" fontWeight="lg" startDecorator={<Avatar size="sm" alt="Affiliation"><VscOrganization /></Avatar>}>
                  Affiliations
                </Typography>
                <Divider />
                <Affiliation />
              </Stack>

              <Stack gap={1}>
                <Typography
                  level="h6"
                  fontWeight="lg"
                  startDecorator={(
                    <Avatar size="sm" alt="CommunityOps">
                      <TbBrandOpenSource />
                    </Avatar>
                  )}
                >
                  Community Ops
                </Typography>
                <Divider />
                <CommunityOps />
              </Stack>

              <Stack gap={1}>
                <Typography
                  level="h6"
                  fontWeight="lg"
                  startDecorator={(
                    <Avatar size="sm" alt="Languages">
                      <IoLanguage />
                    </Avatar>
                    )}
                >
                  Languages
                </Typography>
                <Divider />
                <Stack gap={1}>
                  <Languages />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </>
  );
}
